import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Button } from '@/components/UI/Button'
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/UI/InputGroup'
import useTokenStore from '@/store/store'
import useThemeStore from '@/store/theme'
import { login } from '@/api/login'
import LightLogo from '@/assets/svgs/light-ms-logo.svg'
import DarkLogo from '@/assets/svgs/dark-ms-logo.svg'

const Login = () => {
  const navigate = useNavigate()
  const { setToken } = useTokenStore()
  const { theme } = useThemeStore()
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const onLogin = async () => {
    if (!account || !password) {
      toast.warning('请输入账号和密码')
      return
    }

    try {
      setLoading(true)
      const response = await login({
        username: account,
        password: password
      })

      // 存储token，格式为 "Bearer {access_token}"
      const token = `${response.token_type} ${response.access_token}`
      setToken(token)

      toast.success('登录成功')
      navigate('/home')
    } catch (error) {
      console.error('登录失败', error)
      toast.error(error instanceof Error ? error.message : '登录失败，请检查账号和密码')
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onLogin()
    }
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  const handlePasswordToggleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleTogglePasswordVisibility()
    }
  }

  return (
    <div className="w-full h-screen flex">
      {/* 左侧登录区域 */}
      <div className="flex flex-col gap-10 px-20 pt-15 pb-6 w-640px">
        {/* Logo */}
        <div className="flex items-center">
          <img src={theme === 'dark' ? DarkLogo : LightLogo} alt="Logo" className="h-16" />
        </div>

        {/* 登录表单 */}
        <div className="flex-1 flex flex-col justify-center">
          <div className="flex flex-col gap-8">
            {/* 输入框区域 */}
            <div className="flex flex-col gap-5">
              {/* 账号输入框 */}
              <div className="flex flex-col gap-2">
                <label className="text-h5 text-primary px-1">
                  账号
                </label>
                <InputGroup className="h-12 rounded-3.5">
                  <InputGroupAddon>
                    <iconpark-icon name='outline-user' className='text-primary' size='18' />
                  </InputGroupAddon>
                  <InputGroupInput
                    size="lg"
                    placeholder="请输入账号"
                    value={account}
                    onChange={(e) => setAccount(e.target.value)}
                    onKeyDown={handleKeyDown}
                    aria-label="账号"
                  />
                </InputGroup>
              </div>

              {/* 密码输入框 */}
              <div className="flex flex-col gap-2">
                <label className="text-h5 text-primary px-1">
                  密码
                </label>
                <InputGroup className="h-12 rounded-3.5 pr-3">
                  <InputGroupAddon>
                    <iconpark-icon name='outline-lock' className='text-primary' size='18' />
                  </InputGroupAddon>
                  <InputGroupInput
                    size="lg"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="请输入密码"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    aria-label="密码"
                  />
                  <InputGroupAddon align="inline-end">
                    <Button
                      variant="tertiary"
                      size="mdIcon"
                      aria-label={showPassword ? '隐藏密码' : '显示密码'}
                      tabIndex={0}
                      onClick={handleTogglePasswordVisibility}
                      onKeyDown={handlePasswordToggleKeyDown}
                      className="cursor-pointer text-tertiary hover:text-primary transition-all p-1 rounded-2"
                    >
                      {showPassword ? (
                        <iconpark-icon name='outline-show' color='currentColor' size='16' />
                      ) : (
                        <iconpark-icon name='outline-hide' color='currentColor' size='16' />
                      )}
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </div>

            {/* 登录按钮 */}
            <div className="flex flex-col gap-3 pt-8">
              <Button
                onClick={onLogin}
                disabled={!account || !password || loading}
                size='lgx'
                variant='primary'
                className='text-body-lg-bold'
              >
                {loading ? '登录中...' : '登入'}
              </Button>
            </div>
          </div>
        </div>

        {/* 页脚 */}
        <div className="flex flex-col items-center gap-1 text-tertiary">
          <div className="text-body-md">
            深圳大作家智能技术有限公司
          </div>
          <div className="text-body-sm">
            2025 All rights reserved.
          </div>
        </div>
      </div>

      {/* 右侧背景区域 */}
      <div className="flex-1 relative overflow-hidden">
        {/* 可以在这里添加背景图片或装饰元素 */}
        <div className="absolute inset-0 bg-[url('@/assets/images/bg-brand-big.png')] bg-cover bg-center" />
      </div>
    </div>
  )
}

export default Login
